import { Aide } from '@/components/ui/aide';
import FinancialStatement from '../components/FinancialStatement';
import ShortagePanel from '../components/ShortagePanel';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { driversAPI, trucksAPI } from '../lib/api';
import {
  Plus,
  Users,
  Loader2,
  Search,
  Phone,
  Truck,
  Pencil,
  Trash2,
  Key,
  RefreshCw,
  Copy,
  Check,
  LayoutGrid,
  List,
  ArrowUp,
  ArrowDown,
  History,
} from 'lucide-react';
import { BarrePagination } from '../components/ui/pagination-bar';
import { usePagination } from '../hooks/usePagination';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { SearchableSelect } from '../components/ui/searchable-select';
import { truckOption } from '../lib/selectOptions';
import { fetchAllPages } from '../lib/fetchAllPages';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { ChampsAvances } from '../components/ui/champs-avances';
import { toast } from '../components/ui/sonner';
import { FieldError } from '../components/ui/field-error';
import { cn } from '../lib/utils';
import { PageHeader } from '../components/ui/page-header';
import { ViewSwitcher } from '../components/ui/view-switcher';
import { LoadingState } from '../components/ui/loading-state';
import { useDebouncedValue } from '../hooks/use-debounced-value';
import { EmptyState } from '../components/ui/empty-state';
import { libelle } from '../lib/libelles';
import { effectiveTruckStatus } from '../lib/truckStatus';
import { messageErreur } from '../lib/erreurs';
import { useRafraichirSur } from '../hooks/useRafraichirSur';
import BoutonImportExcel from '../components/fleetImport/BoutonImportExcel';

// Filtres de la liste: valeur de l'adresse (`?filtre=`) -> libellé.
const FILTRES = [
  { value: '', label: 'Tous' },
  { value: 'avec-camion', label: 'Avec un camion' },
  { value: 'sans-camion', label: 'Sans camion' },
  { value: 'disponibles', label: 'Disponibles' },
];
const CODES_FILTRE = FILTRES.map((f) => f.value).filter(Boolean);

// Dispositions de la liste: valeur de l'adresse (`?vue=`).
const VUES = ['tableau', 'cartes'];
const CLE_VUE = 'ecarbu.chauffeurs.vue';
const OPTIONS_VUE = [
  { value: 'tableau', titre: 'Tableau', texteLecteur: 'Afficher en tableau', icone: List, testid: 'drivers-view-tableau' },
  { value: 'cartes', titre: 'Cartes', texteLecteur: 'Afficher en cartes', icone: LayoutGrid, testid: 'drivers-view-cartes' },
];
const lireVueMemorisee = () => {
  try {
    const valeur = window.localStorage.getItem(CLE_VUE);
    return VUES.includes(valeur) ? valeur : null;
  } catch (err) {
    return null;
  }
};
const memoriserVue = (vue) => {
  try {
    window.localStorage.setItem(CLE_VUE, vue);
  } catch (err) {
    // stockage indisponible (navigation privée…): le choix reste dans l'adresse
  }
};

const normaliser = (value) =>
  String(value || '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');

// Statut affiché d'un chauffeur: déduit de son état et du camion affecté.
const STATUTS_CAMION_HORS_SERVICE = [
  'MAINTENANCE',
  'IMMOBILIZED',
  'OUT_OF_SERVICE',
  'RETURNED_TO_OWNER',
];
const STATUTS = {
  DISPONIBLE: { label: 'Disponible', classe: 'bg-success/15 text-success border-success/30' },
  EN_TRAJET: { label: 'En trajet', classe: 'bg-info/15 text-info border-info/30' },
  HORS_SERVICE: { label: 'Hors service', classe: 'bg-muted text-muted-foreground border-border' },
};
const statutChauffeur = (driver, truck) => {
  if (driver.active === false) return 'HORS_SERVICE';
  if (truck) {
    const etat = effectiveTruckStatus(truck);
    if (STATUTS_CAMION_HORS_SERVICE.includes(etat)) return 'HORS_SERVICE';
    if (etat === 'ON_TRIP') return 'EN_TRAJET';
  }
  return 'DISPONIBLE';
};

const COLONNES_TABLEAU = [
  { cle: 'nom', titre: 'Nom' },
  { cle: 'telephone', titre: 'Téléphone' },
  { cle: 'camion', titre: 'Camion affecté' },
  { cle: 'statut', titre: 'Statut' },
  { cle: 'permis', titre: 'Permis' },
  { cle: 'acces', titre: 'Accès mobile' },
  { titre: 'Actions', droite: true },
];

const PastilleStatut = ({ statut }) => (
  <Badge className={cn('status-badge border text-xs', STATUTS[statut].classe)}>
    {STATUTS[statut].label}
  </Badge>
);

const DriversPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [drivers, setDrivers] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  // Le squelette ne remplace la page qu'au tout premier chargement:
  // un rafraîchissement démontait le champ de recherche en cours de frappe.
  const [hasLoaded, setHasLoaded] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebouncedValue(searchQuery);
  const [tri, setTri] = useState({ cle: null, sens: 'asc' });
  const [vueMemorisee] = useState(lireVueMemorisee);
  const [showNewModal, setShowNewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [driverToEdit, setDriverToEdit] = useState(null);
  const [historyDriver, setHistoryDriver] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [driverToDelete, setDriverToDelete] = useState(null);
  const [driverToAssign, setDriverToAssign] = useState(null);
  const [truckToAssign, setTruckToAssign] = useState('none_selected');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newSubmitAttempted, setNewSubmitAttempted] = useState(false);
  const [editSubmitAttempted, setEditSubmitAttempted] = useState(false);
  const [accessCodeToShow, setAccessCodeToShow] = useState(null); // {code, name}
  const [copied, setCopied] = useState(false);
  const [confirmRegen, setConfirmRegen] = useState(null); // driver
  const [formData, setFormData] = useState({
    full_name: '',
    phone: '',
    license_number: '',
    assigned_truck_id: 'none_selected',
    active: true,
  });

  const canCreate = true;

  const rawFiltre = searchParams.get('filtre');
  const filtre = CODES_FILTRE.includes(rawFiltre) ? rawFiltre : '';
  const rawVue = searchParams.get('vue');
  const vue = VUES.includes(rawVue) ? rawVue : vueMemorisee || 'tableau';

  const updateParams = useCallback(
    (changes) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          Object.entries(changes).forEach(([key, value]) => {
            if (value === null || value === undefined || value === '') next.delete(key);
            else next.set(key, String(value));
          });
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const changerVue = useCallback(
    (valeur) => {
      memoriserVue(valeur);
      updateParams({ vue: valeur });
    },
    [updateParams],
  );

  const changerFiltre = useCallback(
    (valeur) => {
      updateParams({ filtre: valeur });
    },
    [updateParams],
  );

  // Tous les chauffeurs sont chargés: filtres, compteurs, tri et pages se
  // font dans le navigateur, sur la liste complète.
  const loadData = useCallback(async ({ silencieux = false } = {}) => {
    try {
      if (!silencieux) setIsLoading(true);
      const result = await fetchAllPages((params) => driversAPI.getAllPaged(params));
      setDrivers(result.items || []);
    } catch (err) {
      console.error('Failed to load drivers:', err);
      toast.error(messageErreur(err, 'Impossible de charger les chauffeurs'));
    } finally {
      setIsLoading(false);
      setHasLoaded(true);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Le camion d'un chauffeur se choisit parmi tout le parc (le millième
  // camion doit se retrouver à la recherche).
  const loadTrucks = useCallback(async () => {
    try {
      const result = await fetchAllPages((params) => trucksAPI.getAllPaged(params));
      setTrucks(result.items || []);
    } catch (err) {
      console.error('Failed to load trucks:', err);
      toast.error(messageErreur(err, 'Impossible de charger les camions'));
    }
  }, []);

  useEffect(() => {
    loadTrucks();
  }, [loadTrucks]);

  // Une écriture faite ailleurs (autre écran, autre onglet, file hors ligne rejouée) recharge ces chiffres: jamais de compteur figé.
  useRafraichirSur(['chauffeurs', 'camions', 'documents', 'repartition', 'trajets'], (options) => Promise.all([loadData(options), loadTrucks(options)]));


  // Une affectation change le chauffeur affiché sous chaque camion.
  const refreshAll = () => Promise.all([loadData(), loadTrucks()]);

  const handleSubmit = async () => {
    setNewSubmitAttempted(true);
    if (!formData.full_name || !formData.phone) {
      toast.error('Veuillez remplir les champs requis');
      return;
    }

    setIsSubmitting(true);
    try {
      // `active` n'est pas envoyé: `drivers.py` le force à vrai à la création
      // et écrase ce que l'écran propose. Le champ a donc disparu du
      // formulaire — un interrupteur qui ne décide rien laisse croire qu'on
      // peut enregistrer un chauffeur déjà désactivé.
      const created = await driversAPI.create({
        full_name: formData.full_name,
        phone: formData.phone,
        license_number: formData.license_number || null,
        assigned_truck_id:
          formData.assigned_truck_id === 'none_selected'
            ? null
            : formData.assigned_truck_id,
      });
      setShowNewModal(false);
      toast.success('Chauffeur créé');
      // Show access code modal if generated
      if (created?.access_code_once) {
        setAccessCodeToShow({
          code: created.access_code_once,
          name: created.full_name || formData.full_name,
        });
      }
      setFormData({
        full_name: '',
        phone: '',
        license_number: '',
        assigned_truck_id: 'none_selected',
        active: true,
      });
      setNewSubmitAttempted(false);
      await refreshAll();
    } catch (err) {
      console.error('Failed to create driver:', err);
      toast.error(messageErreur(err, 'Impossible de créer le chauffeur'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRegenerateCode = async (driver) => {
    setIsSubmitting(true);
    try {
      const data = await driversAPI.regenerateCode(driver.id);
      const newCode = data?.access_code || data?.data?.access_code;
      if (newCode) {
        setAccessCodeToShow({ code: newCode, name: driver.full_name });
        toast.success('Nouveau code généré');
      }
      setConfirmRegen(null);
      await refreshAll();
    } catch (err) {
      console.error('Failed to regenerate code:', err);
      toast.error(messageErreur(err, 'Impossible de générer un nouveau code'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const copyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      toast.error('Impossible de copier');
    }
  };


  const openEdit = (driver) => {
    setDriverToEdit(driver);
    setEditSubmitAttempted(false);
    setFormData({
      full_name: driver.full_name || '',
      phone: driver.phone || '',
      license_number: driver.license_number || '',
      assigned_truck_id: driver.assigned_truck_id || 'none_selected',
      active: !!driver.active,
    });
    setShowEditModal(true);
  };

  const handleEditSubmit = async () => {
    if (!driverToEdit) return;
    setEditSubmitAttempted(true);
    if (!formData.full_name || !formData.phone) {
      toast.error('Veuillez remplir les champs requis');
      return;
    }

    setIsSubmitting(true);
    try {
      await driversAPI.update(driverToEdit.id, {
        ...formData,
        license_number: formData.license_number || null,
        assigned_truck_id:
          formData.assigned_truck_id === 'none_selected'
            ? null
            : formData.assigned_truck_id,
      });
      setShowEditModal(false);
      setDriverToEdit(null);
      toast.success('Chauffeur mis à jour');
      setEditSubmitAttempted(false);
      await refreshAll();
    } catch (err) {
      console.error('Failed to update driver:', err);
      toast.error(messageErreur(err, 'Impossible de modifier le chauffeur'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const confirmDelete = (driver) => {
    setDriverToDelete(driver);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!driverToDelete) return;
    setIsSubmitting(true);
    try {
      await driversAPI.delete(driverToDelete.id);
      setShowDeleteModal(false);
      setDriverToDelete(null);
      toast.success('Chauffeur supprimé');
      await refreshAll();
    } catch (err) {
      console.error('Failed to delete driver:', err);
      toast.error(messageErreur(err, 'Impossible de supprimer le chauffeur'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const truckOptions = useMemo(
    () => [
      { value: 'none_selected', label: 'Aucun camion' },
      ...trucks.map((t) => ({
        ...truckOption(t),
        description:
          [
            t.current_status ? libelle('truckStatus', t.current_status) : null,
            t.driver_full_name,
          ]
            .filter(Boolean)
            .join(' · ') || undefined,
      })),
    ],
    [trucks]
  );

  const openAssign = (driver) => {
    setDriverToAssign(driver);
    setTruckToAssign(driver.assigned_truck_id || 'none_selected');
  };

  const handleAssign = async () => {
    if (!driverToAssign) return;
    setIsSubmitting(true);
    try {
      await driversAPI.update(driverToAssign.id, {
        assigned_truck_id: truckToAssign === 'none_selected' ? null : truckToAssign,
      });
      setDriverToAssign(null);
      toast.success('Camion affecté');
      await refreshAll();
    } catch (err) {
      console.error('Failed to assign truck:', err);
      toast.error(messageErreur(err, 'Impossible d’affecter le camion'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const trucksById = useMemo(() => {
    const map = new Map();
    trucks.forEach((t) => t?.id && map.set(t.id, t));
    return map;
  }, [trucks]);

  // Chaque chauffeur avec son camion et son statut, calculés une fois.
  const rows = useMemo(
    () =>
      drivers.map((driver) => {
        const truck = driver.assigned_truck_id ? trucksById.get(driver.assigned_truck_id) : null;
        return { driver, truck, statut: statutChauffeur(driver, truck) };
      }),
    [drivers, trucksById],
  );

  const compteurs = useMemo(() => {
    const avec = rows.filter((r) => r.driver.assigned_truck_id).length;
    return {
      tous: rows.length,
      'avec-camion': avec,
      'sans-camion': rows.length - avec,
      disponibles: rows.filter((r) => r.statut === 'DISPONIBLE').length,
    };
  }, [rows]);

  const filteredRows = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    const qPlaque = normaliser(debouncedSearch);
    return rows.filter(({ driver, truck, statut }) => {
      if (filtre === 'avec-camion' && !driver.assigned_truck_id) return false;
      if (filtre === 'sans-camion' && driver.assigned_truck_id) return false;
      if (filtre === 'disponibles' && statut !== 'DISPONIBLE') return false;
      if (!q) return true;
      if (String(driver.full_name || '').toLowerCase().includes(q)) return true;
      if (String(driver.phone || '').toLowerCase().includes(q)) return true;
      return (
        qPlaque !== '' &&
        [truck?.matricule, truck?.tank_number].some((v) => normaliser(v).includes(qPlaque))
      );
    });
  }, [rows, debouncedSearch, filtre]);

  const CLES_TRI = useMemo(
    () => ({
      nom: ({ driver }) => String(driver.full_name || '').toLowerCase(),
      telephone: ({ driver }) => String(driver.phone || ''),
      camion: ({ truck }) => normaliser(truck?.matricule),
      statut: ({ statut }) => STATUTS[statut].label,
      permis: ({ driver }) => String(driver.license_number || '').toLowerCase(),
      acces: ({ driver }) => (driver.access_code ? 'Actif' : 'Aucun'),
    }),
    [],
  );

  const sortedRows = useMemo(() => {
    const lire = CLES_TRI[tri.cle];
    if (!lire) return filteredRows;
    const sens = tri.sens === 'desc' ? -1 : 1;
    return [...filteredRows].sort(
      (a, b) => String(lire(a)).localeCompare(String(lire(b)), 'fr', { numeric: true }) * sens,
    );
  }, [filteredRows, tri, CLES_TRI]);

  // Pagination commune: page et taille dans l'adresse, barre sous la liste.
  const pagination = usePagination(sortedRows, { reinitialiser: `${debouncedSearch}|${filtre}` });
  const pagedRows = pagination.lignes;

  const trierPar = (cle) =>
    setTri((courant) =>
      courant.cle === cle
        ? { cle, sens: courant.sens === 'asc' ? 'desc' : 'asc' }
        : { cle, sens: 'asc' },
    );

  const statsChauffeurs = [
    {
      label: 'Chauffeurs',
      valeur: compteurs.tous,
      onClick: () => changerFiltre(''),
      aide: 'Afficher tous les chauffeurs',
    },
    {
      label: 'Disponibles',
      valeur: compteurs.disponibles,
      ton: 'positif',
      onClick: () => changerFiltre('disponibles'),
      aide: 'Afficher uniquement les chauffeurs disponibles',
    },
    {
      label: 'Avec un camion',
      valeur: compteurs['avec-camion'],
      onClick: () => changerFiltre('avec-camion'),
      aide: 'Afficher uniquement les chauffeurs avec un camion',
    },
    {
      label: 'Sans camion',
      valeur: compteurs['sans-camion'],
      ton: compteurs['sans-camion'] ? 'attention' : 'neutre',
      onClick: () => changerFiltre('sans-camion'),
      aide: 'Afficher uniquement les chauffeurs sans camion',
    },
  ];

  const lienCamion = (truck) => (
    <Link
      to={`/trucks/${truck.id}`}
      className="inline-flex flex-wrap items-center gap-x-2 hover:underline"
      onClick={(e) => e.stopPropagation()}
      data-testid={`driver-truck-link-${truck.id}`}
    >
      <span className="matricule-plate text-xs">{truck.matricule}</span>
      {truck.tank_number ? (
        <span className="text-xs font-bold text-muted-foreground">{truck.tank_number}</span>
      ) : null}
    </Link>
  );

  const renderActions = (driver) => (
    <div className="flex items-center gap-0.5">
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-8 w-8"
        onClick={() => setHistoryDriver(driver)}
        title="Historique et compte"
        aria-label="Historique et compte du chauffeur"
      >
        <History className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-8 w-8"
        onClick={() => openAssign(driver)}
        title="Affecter un camion"
        aria-label="Affecter un camion"
        data-testid={`assign-driver-${driver.id}`}
      >
        <Truck className="h-4 w-4" />
      </Button>
      {driver.access_code && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-flame-ink"
          onClick={() => setConfirmRegen(driver)}
          title="Régénérer le code"
          aria-label="Régénérer le code d'accès"
          data-testid={`regen-driver-${driver.id}`}
        >
          <RefreshCw className="h-4 w-4" />
        </Button>
      )}
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-8 w-8"
        onClick={() => openEdit(driver)}
        aria-label="Modifier le chauffeur"
        data-testid={`edit-driver-${driver.id}`}
      >
        <Pencil className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-8 w-8 text-destructive hover:text-destructive"
        onClick={() => confirmDelete(driver)}
        aria-label="Supprimer le chauffeur"
        data-testid={`delete-driver-${driver.id}`}
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );

  const badgeAcces = (driver) =>
    driver.access_code ? (
      <span className="inline-flex items-center gap-1 text-xs text-success">
        <Key className="h-3 w-3" aria-hidden="true" />
        Code actif
      </span>
    ) : (
      <span className="text-xs text-muted-foreground">Aucun code</span>
    );

  if (isLoading && !hasLoaded) {
    return (
      <div className="mx-auto w-full max-w-page space-y-5 px-4 py-5 md:px-6 md:py-6 lg:px-8">
        <LoadingState shape="cards" />
      </div>
    );
  }

  const filtreActif = Boolean(filtre || debouncedSearch.trim());

  return (
    <div className="mx-auto w-full max-w-[1800px] space-y-5 px-4 py-5 md:px-6 md:py-6 lg:px-8" data-testid="drivers-page">
      <PageHeader
        eyebrow="Ressources"
        title="Chauffeurs"
        stats={statsChauffeurs}
        statsDisposition="ligne"
        actions={
          <>
            <BoutonImportExcel />
            {canCreate && (
              <Button
                onClick={() => setShowNewModal(true)}
                data-testid="new-driver-btn"
              >
                <Plus className="h-4 w-4" aria-hidden="true" />
                Nouveau chauffeur
              </Button>
            )}
          </>
        }
      />

      {/* Barre d'outils: recherche, filtres, puis disposition et pagination à droite */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2" data-testid="drivers-toolbar">
        <div className="relative w-full md:w-[30%] md:max-w-sm lg:w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground/70" />
          <Input
            placeholder="Rechercher par nom, téléphone ou plaque…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input-base pl-10"
            data-testid="drivers-search"
          />
        </div>

        <div
          role="group"
          aria-label="Filtrer les chauffeurs"
          className="flex flex-wrap items-center gap-2"
          data-testid="drivers-filter"
        >
          {FILTRES.map((option) => {
            const active = filtre === option.value;
            const count = compteurs[option.value || 'tous'];
            return (
              <Button
                key={option.value || 'tous'}
                type="button"
                size="sm"
                variant={active ? 'default' : 'outline'}
                aria-pressed={active}
                onClick={() => changerFiltre(option.value)}
                data-testid={`drivers-filter-${option.value || 'tous'}`}
              >
                {option.label} {count}
              </Button>
            );
          })}
        </div>

        <div
          className="flex flex-wrap items-center gap-2 md:ml-auto"
          data-testid="drivers-layout"
        >
          <ViewSwitcher
            value={vue}
            onChange={changerVue}
            options={OPTIONS_VUE}
            ariaLabel="Disposition de la liste"
            testid="drivers-view-switcher"
          />
        </div>
      </div>

      {pagedRows.length === 0 ? (
        <EmptyState
          variant={filtreActif ? 'filtered' : 'empty'}
          icon={Users}
          title={filtreActif ? 'Aucun résultat' : 'Aucun chauffeur. Ajoutez le premier.'}
          description={
            filtreActif
              ? 'Aucun chauffeur ne correspond à cette recherche ou à ce filtre.'
              : 'Un chauffeur enregistré reçoit un code à six chiffres qui lui ouvre l’application mobile : c’est par là qu’arrivent les relevés du terrain.'
          }
          actionLabel={filtreActif ? undefined : 'Ajouter un chauffeur'}
          onAction={() => setShowNewModal(true)}
        />
      ) : vue === 'tableau' ? (
        <div className="rounded-lg border border-border bg-card" data-testid="drivers-table">
          <Table>
            <TableHeader>
              <TableRow>
                {COLONNES_TABLEAU.map((colonne) => {
                  const actif = tri.cle === colonne.cle;
                  return (
                    <TableHead
                      key={colonne.cle || colonne.titre}
                      className={colonne.droite ? 'text-right' : undefined}
                      aria-sort={
                        actif ? (tri.sens === 'asc' ? 'ascending' : 'descending') : undefined
                      }
                    >
                      {colonne.cle ? (
                        <button
                          type="button"
                          onClick={() => trierPar(colonne.cle)}
                          className="inline-flex items-center gap-1 uppercase hover:text-foreground"
                          data-testid={`drivers-sort-${colonne.cle}`}
                        >
                          {colonne.titre}
                          {actif ? (
                            tri.sens === 'asc' ? (
                              <ArrowUp className="h-3 w-3" aria-hidden="true" />
                            ) : (
                              <ArrowDown className="h-3 w-3" aria-hidden="true" />
                            )
                          ) : null}
                        </button>
                      ) : (
                        <span className="sr-only">{colonne.titre}</span>
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            </TableHeader>
            <TableBody>
              {pagedRows.map(({ driver, truck, statut }, index) => (
                <TableRow
                  key={driver.id}
                  className={cn(index % 2 === 1 && 'bg-muted/30', driver.active === false && 'opacity-70')}
                  data-testid={`driver-row-${driver.id}`}
                >
                  <TableCell className="whitespace-nowrap font-semibold">{driver.full_name}</TableCell>
                  <TableCell className="whitespace-nowrap text-muted-foreground">{driver.phone || '—'}</TableCell>
                  <TableCell className="whitespace-nowrap">
                    {truck ? (
                      lienCamion(truck)
                    ) : driver.assigned_truck_id ? (
                      <span className="text-muted-foreground">Camion affecté</span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <PastilleStatut statut={statut} />
                  </TableCell>
                  <TableCell className="whitespace-nowrap font-mono text-xs text-muted-foreground">
                    {driver.license_number || '—'}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">{badgeAcces(driver)}</TableCell>
                  <TableCell className="text-right">{renderActions(driver)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div
          className="go-sm2-lg3-2xl4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4"
          data-testid="drivers-cards"
        >
          {pagedRows.map(({ driver, truck, statut }) => (
            <Card
              key={driver.id}
              className={cn('card-base group', driver.active === false && 'opacity-70')}
              data-testid={`driver-card-${driver.id}`}
            >
              <CardContent className="space-y-2 p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 space-y-1">
                    <h3 className="truncate text-base font-bold text-foreground">{driver.full_name}</h3>
                    <PastilleStatut statut={statut} />
                  </div>
                  {/* Toujours visibles sur mobile; au survol ailleurs. */}
                  <div className="shrink-0 transition-opacity lg:opacity-0 lg:group-hover:opacity-100 lg:group-focus-within:opacity-100">
                    {renderActions(driver)}
                  </div>
                </div>
                <div className="space-y-1 text-sm text-muted-foreground">
                  <p className="inline-flex items-center gap-1.5">
                    <Phone className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                    {driver.phone || '—'}
                  </p>
                  <p className="flex items-center gap-1.5">
                    <Truck className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                    {truck ? (
                      lienCamion(truck)
                    ) : driver.assigned_truck_id ? (
                      'Camion affecté'
                    ) : (
                      'Aucun camion'
                    )}
                  </p>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    {driver.license_number ? (
                      <span className="font-mono text-xs">Permis {driver.license_number}</span>
                    ) : null}
                    {badgeAcces(driver)}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {pagedRows.length > 0 ? <BarrePagination pagination={pagination} nom="chauffeurs" /> : null}

      {/* Affecter un camion */}
      <Dialog open={!!driverToAssign} onOpenChange={(open) => !open && setDriverToAssign(null)}>
        <DialogContent className="sm:max-w-md" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>Affecter un camion à {driverToAssign?.full_name}</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label className="label-base">Camion</Label>
            <SearchableSelect
              className="input-base"
              data-testid="assign-driver-truck"
              placeholder="Sélectionner un camion"
              searchPlaceholder="Rechercher une immatriculation…"
              emptyMessage="Aucun camion trouvé"
              value={truckToAssign || ''}
              onValueChange={setTruckToAssign}
              options={truckOptions}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDriverToAssign(null)}>
              Annuler
            </Button>
            <Button
              className="btn-primary"
              onClick={handleAssign}
              disabled={isSubmitting}
              data-testid="submit-assign-driver"
            >
              {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Driver Modal */}
      <Dialog open={!!historyDriver} onOpenChange={open => !open && setHistoryDriver(null)}><DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto"><DialogHeader><DialogTitle>{historyDriver?.full_name} · historique et compte</DialogTitle></DialogHeader>{historyDriver && <><ShortagePanel driverId={historyDriver.id}/><FinancialStatement partyType="DRIVER" partyId={historyDriver.id}/></>}</DialogContent></Dialog>
      <Dialog
        open={showNewModal}
        onOpenChange={(open) => {
          setShowNewModal(open);
          if (!open) setNewSubmitAttempted(false);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Nouveau chauffeur</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label className="label-base">Nom complet *</Label>
              <Input
                placeholder="Ex: Mamadou Diallo"
                value={formData.full_name}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    full_name: e.target.value,
                  }))
                }
                className="input-base"
                data-testid="new-driver-name"
              />
              <FieldError show={newSubmitAttempted && !formData.full_name}>
                Nom complet requis
              </FieldError>
            </div>

            <div>
              <Label className="label-base">Téléphone *</Label>
              <Input
                placeholder="Ex: +221 77 123 4567"
                value={formData.phone}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, phone: e.target.value }))
                }
                className="input-base"
                data-testid="new-driver-phone"
              />
              <FieldError show={newSubmitAttempted && !formData.phone}>
                Téléphone requis
              </FieldError>
            </div>

            {/* Le nom et le téléphone suffisent: c'est d'eux que le serveur
                tire le compte du chauffeur et son code à six chiffres. Le
                permis et le camion habituel n'entrent dans aucune règle —
                ils attendent derrière le dépliant. */}
            <ChampsAvances
              consequence="Facultatifs : le chauffeur reçoit son code d’accès sans eux."
              valeurs={[
                formData.license_number,
                // 'none_selected' est la valeur « aucun camion »: la traiter
                // comme une saisie ouvrirait le dépliant à chaque création.
                formData.assigned_truck_id === 'none_selected'
                  ? ''
                  : formData.assigned_truck_id,
              ]}
            >
              <div>
                <Label className="label-base">Numéro de permis</Label>
                <Input
                  placeholder="Ex: DL-SN-001234"
                  value={formData.license_number}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      license_number: e.target.value,
                    }))
                  }
                  className="input-base font-mono"
                  data-testid="new-driver-license"
                />
              </div>

              <div>
                <div className="flex items-center gap-1">
                  <Label className="label-base">
                    Affecter à un camion (optionnel)
                  </Label>
                  <Aide titre="Affectation habituelle">
                    L’affectation habituelle se règle depuis la fiche du camion :
                    le nom et le téléphone saisis là-bas y créent le chauffeur et
                    son compte.
                  </Aide>
                </div>
                <SearchableSelect
                  className="input-base"
                  data-testid="new-driver-truck"
                  placeholder="Sélectionner un camion"
                  searchPlaceholder="Rechercher une immatriculation…"
                  emptyMessage="Aucun camion trouvé"
                  value={formData.assigned_truck_id || ''}
                  onValueChange={(v) =>
                    setFormData((prev) => ({ ...prev, assigned_truck_id: v }))
                  }
                  options={truckOptions}
                />
                {/* Le même lien se saisissait des deux côtés, dans deux
                    formats: un nom libre sur la fiche du camion, un
                    identifiant ici. On dit lequel fait foi plutôt que de
                    laisser l'exploitant remplir les deux. */}
              </div>
            </ChampsAvances>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewModal(false)}>
              Annuler
            </Button>
            <Button
              className="btn-primary"
              onClick={handleSubmit}
              disabled={
                isSubmitting ||
                !formData.full_name ||
                !formData.phone
              }
              data-testid="submit-new-driver"
            >
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                'Créer'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Driver Modal */}
      <Dialog
        open={showEditModal}
        onOpenChange={(open) => {
          setShowEditModal(open);
          if (!open) setEditSubmitAttempted(false);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Modifier le chauffeur</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label className="label-base">Nom complet *</Label>
              <Input
                placeholder="Ex: Mamadou Diallo"
                value={formData.full_name}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    full_name: e.target.value,
                  }))
                }
                className="input-base"
              />
              <FieldError show={editSubmitAttempted && !formData.full_name}>
                Nom complet requis
              </FieldError>
            </div>

            <div>
              <Label className="label-base">Téléphone *</Label>
              <Input
                placeholder="Ex: +221 77 123 4567"
                value={formData.phone}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, phone: e.target.value }))
                }
                className="input-base"
              />
              <FieldError show={editSubmitAttempted && !formData.phone}>
                Téléphone requis
              </FieldError>
            </div>

            {/* « Actif » reste ici, et seulement ici: désactiver un chauffeur
                qui a quitté la société est une décision, contrairement à la
                création où le serveur impose déjà vrai. */}
            <div className="flex items-center justify-between">
              <Label className="label-base mb-0">Actif</Label>
              <Switch
                checked={formData.active}
                onCheckedChange={(checked) =>
                  setFormData((prev) => ({ ...prev, active: checked }))
                }
              />
            </div>

            <ChampsAvances
              consequence="Facultatifs : ni le code d’accès ni les trajets n’en dépendent."
              valeurs={[
                formData.license_number,
                formData.assigned_truck_id === 'none_selected'
                  ? ''
                  : formData.assigned_truck_id,
              ]}
            >
              <div>
                <Label className="label-base">Numéro de permis (optionnel)</Label>
                <Input
                  placeholder="Ex: DL-SN-001234"
                  value={formData.license_number}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      license_number: e.target.value,
                    }))
                  }
                  className="input-base font-mono"
                />
              </div>

              <div>
                <div className="flex items-center gap-1">
                  <Label className="label-base">
                    Affecter à un camion (optionnel)
                  </Label>
                  <Aide titre="Affectation habituelle">
                    L’affectation habituelle se règle depuis la fiche du camion :
                    le nom et le téléphone saisis là-bas y créent le chauffeur et
                    son compte.
                  </Aide>
                </div>
                <SearchableSelect
                  className="input-base"
                  placeholder="Sélectionner un camion"
                  searchPlaceholder="Rechercher une immatriculation…"
                  emptyMessage="Aucun camion trouvé"
                  value={formData.assigned_truck_id || ''}
                  onValueChange={(v) =>
                    setFormData((prev) => ({ ...prev, assigned_truck_id: v }))
                  }
                  options={truckOptions}
                />
              </div>
            </ChampsAvances>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowEditModal(false);
                setDriverToEdit(null);
              }}
            >
              Annuler
            </Button>
            <Button
              className="btn-primary"
              onClick={handleEditSubmit}
              disabled={
                isSubmitting ||
                !formData.full_name ||
                !formData.phone
              }
            >
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                'Enregistrer'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Access code modal */}
      <Dialog
        open={!!accessCodeToShow}
        onOpenChange={(open) => {
          if (!open) setAccessCodeToShow(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-flame" />
              Code d'accès chauffeur
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-sm text-muted-foreground">
              Communiquez ce code à <strong>{accessCodeToShow?.name}</strong>{' '}
              pour la connexion à l'application mobile. Il ne sera plus visible
              en clair après la fermeture.
            </p>
            <div className="flex items-center gap-2 p-4 bg-muted/50 rounded-lg border border-border">
              <code className="flex-1 text-2xl font-mono font-bold tracking-widest text-center text-flame">
                {accessCodeToShow?.code}
              </code>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => copyCode(accessCodeToShow?.code)}
              >
                {copied ? (
                  <Check className="w-4 h-4 text-success" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button
              className="btn-primary"
              onClick={() => setAccessCodeToShow(null)}
            >
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Regenerate code confirm */}
      <AlertDialog open={!!confirmRegen} onOpenChange={(open) => !open && setConfirmRegen(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Régénérer le code d'accès ?</AlertDialogTitle>
            <AlertDialogDescription>
              L'ancien code sera invalidé. Le chauffeur devra utiliser le
              nouveau code pour se connecter à l'application mobile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmRegen && handleRegenerateCode(confirmRegen)}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                'Régénérer'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete confirm */}
      <AlertDialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce chauffeur ?</AlertDialogTitle>
            <AlertDialogDescription>
              Les trajets et l’historique liés à ce chauffeur seront aussi
              supprimés.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setDriverToDelete(null);
              }}
            >
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={isSubmitting}>
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                'Supprimer'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};


export default DriversPage;
